import { uniq } from 'lodash';
import sortBy from 'lodash/sortBy';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatDayFromUtc } from '../../../utils/date';
import { roundValue } from '../../../utils/math';
import { queryHashGenerator } from '../../../utils/queryHash';
import {
  EntityType,
  IComputedAggregate,
  IMetric,
  ISelectableTag,
} from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { IInclusiveDateOption } from '../settings/dates/dateRangeProvider';
import {
  IRatioReportSettings,
  IRatioReportSettingsState,
  ratioReportSettingsProvider,
} from '../settings/reports/ratioReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { formatTimeframe } from '../utils/formatTimeframe';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportResult, IReportState } from './types';

export interface IRatio {
  timestamp: number;
  ratio: number;
  metricOne: {
    metric: IMetric;
    label: string;
  };
  metricTwo: {
    metric: IMetric;
    label: string;
  };
}

export interface IRatioResult extends IReportResult {
  ratio: number;
  percentageChange: number | null;
  totalChange: number | null;
  metricOne: {
    metric: IMetric;
    label: string;
  };
  metricTwo: {
    metric: IMetric;
    label: string;
  };
  displayDate: string;
  timestamp: number;
}

export interface IRatioReportState extends IReportState {
  settings: ISettingsTemplate<IRatioReportSettings>;
  ratioList: IRatio[];
  resultList: IRatioResult[];
  timeframe: string;
}

export const EUR_TEMPLATE_ID = 'OrDji4OaMC2pwevSXEQO';
export const DSI_TEMPLATE_ID = 'msXZOTAn3OP0qqr6DkEJ';

export const isEurOrDsiTemplate = (id: string): boolean =>
  [EUR_TEMPLATE_ID, DSI_TEMPLATE_ID].includes(id);

export const extractSpecialRatioTags = (
  id: string,
  settings: IRatioReportSettingsState,
  key: 'metricOneTags' | 'metricTwoTags'
): ISelectableTag[] => {
  if (id === EUR_TEMPLATE_ID) return settings?.eur?.[key];
  if (id === DSI_TEMPLATE_ID) return settings?.dsi?.[key];
  return [];
};

/**
 * @description pipeline that observes the aggregates requested based on the Dsi report settings
 * and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const ratioReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<IRatioReportState> => {
  return ratioReportSettingsProvider().pipe(
    /// observe the aggregates as defined in the settings
    switchMap(
      (
        settingsState: IRatioReportSettingsState
      ): Observable<
        [IRatioReportSettingsState, ICrossTestAggregates, IMetricMeta]
      > => {
        /// validate settings before fetching data
        if (validateSettings(settingsState).isValid) {
          const {
            timeframe,
            selected: {
              settings: { metricOne, metricTwo },
            },
          } = settingsState;

          /// date type is always `custom`
          const dateType: IInclusiveDateOption = {
            label: 'Custom',
            value: 'custom',
          };

          /// create query hash for metric one
          const metricOneHash = isEurOrDsiTemplate(settingsState?.selected?.id)
            ? queryHashGenerator(
                metricOne.testType.entity.id as string,
                (
                  extractSpecialRatioTags(
                    settingsState.selected?.id,
                    settingsState,
                    'metricOneTags'
                  ) ?? []
                ).map((t: ISelectableTag): string => t.value)
              )
            : queryHashGenerator(
                metricOne.testType.entity.id as string,
                (metricOne.tags ?? []).map(
                  (t: ISelectableTag): string => t.value
                )
              );
          /// create query hash for metric two
          const metricTwoHash = isEurOrDsiTemplate(settingsState?.selected?.id)
            ? queryHashGenerator(
                metricTwo.testType.entity.id as string,
                (
                  extractSpecialRatioTags(
                    settingsState.selected?.id,
                    settingsState,
                    'metricTwoTags'
                  ) ?? []
                ).map((t: ISelectableTag): string => t.value)
              )
            : queryHashGenerator(
                metricTwo.testType.entity.id as string,
                (metricTwo.tags ?? []).map(
                  (t: ISelectableTag): string => t.value
                )
              );

          const metricMeta: IMetricMeta = {
            metricOne: {
              hash: metricOneHash,
              entity: metricOne.metric.entity,
              label: `${metricOne.testType.entity.abbr} ${metricOne.metric.label}`,
            },
            metricTwo: {
              hash: metricTwoHash,
              entity: metricTwo.metric.entity,
              label: `${metricTwo.testType.entity.abbr} ${metricTwo.metric.label}`,
            },
          };

          return combineLatest<
            [IRatioReportSettingsState, ICrossTestAggregates, IMetricMeta]
          >([
            of<IRatioReportSettingsState>(settingsState),
            crossTestAggregates(
              [metricOneHash, metricTwoHash],
              entityId,
              entityType,
              dateType,
              timeframe
            ),
            of(metricMeta),
          ]);
        } else {
          return combineLatest([of(settingsState), of(null), of(null)]);
        }
      }
    ),
    /// map the aggregates to a state
    map(
      (
        result: [IRatioReportSettingsState, ICrossTestAggregates, IMetricMeta]
      ): IRatioReportState => {
        const [settingsState, aggregates, metricMeta] = result;
        const ratioList = generateRatioList(
          aggregates,
          metricMeta,
          settingsState.selected?.id,
          settingsState.filter
        );
        const ratioResultList = generateRatioResultList(ratioList);
        return {
          ...validateState(settingsState, ratioResultList),
          settings: settingsState.selected,
          ratioList,
          resultList: ratioResultList,
          timeframe: formatTimeframe(
            settingsState.timeframe?.from?.value,
            settingsState.timeframe?.to?.value
          ),
        };
      }
    )
  );
};

/**
 * @description generates the result list by breaking the data into quartiles
 * @param ratioList
 * @returns
 */
const generateRatioResultList = (ratioList: IRatio[]): IRatioResult[] => {
  if (ratioList.length === 0) return [];
  const sortedRatioList = sortBy(
    ratioList,
    (dsi: IRatio): number => -dsi.timestamp
  );
  const results: IRatioResult[] = [];

  for (let i = 0; i < sortedRatioList.length; i++) {
    const ratio = sortedRatioList[i];
    let change;
    if (i < sortedRatioList.length - 1) {
      const nearestComparableRatio = sortedRatioList
        .slice(i + 1)
        .find((d: IRatio): boolean => d.ratio !== null);

      change = calculateChange(
        nearestComparableRatio?.ratio ?? null,
        ratio.ratio ?? null
      );
    }
    results.push({
      ...ratio,
      ...change,
      displayDate: formatDayFromUtc(ratio.timestamp),
    });
  }
  return results;
};

/**
 * @description calculates the change in dsi values between quartiles
 * @param previousRatio
 * @param currentRatio
 * @returns
 */
const calculateChange = (
  previousRatio: number | null,
  currentRatio: number | null
): { percentageChange: number | null; totalChange: number | null } => {
  if (currentRatio !== null && previousRatio !== null) {
    const totalChange = roundValue(currentRatio - previousRatio, 2);
    return {
      totalChange,
      percentageChange: roundValue((totalChange / previousRatio) * 100, 1),
    };
  } else {
    return {
      totalChange: null,
      percentageChange: null,
    };
  }
};

/**
 * @description
 * @param aggregates
 * @parammetricMeta
 * @returns
 */
const generateRatioList = (
  aggregates: ICrossTestAggregates,
  metricMeta: IMetricMeta,
  templateId: string,
  filter: boolean
): IRatio[] => {
  if (!metricMeta?.metricOne.hash || !metricMeta?.metricTwo.hash) return [];
  const metricOneResults = aggregates[metricMeta?.metricOne.hash];
  const metricTwoResults = aggregates[metricMeta?.metricTwo.hash];

  const metricOneSessionIds = metricOneResults.sessions.map(
    (ca: IComputedAggregate): string => ca.meta.id
  );

  const metricTwoSessionIds = metricTwoResults.sessions.map(
    (ca: IComputedAggregate): string => ca.meta.id
  );

  const uniqueSessionIds = uniq([
    ...metricOneSessionIds,
    ...metricTwoSessionIds,
  ]);

  const allData = uniqueSessionIds.map((sessionId: string): IRatio => {
    const one = metricOneResults.sessions.find(
      (ca: IComputedAggregate): boolean => ca.meta.id === sessionId
    ) as IComputedAggregate;
    const two = metricTwoResults.sessions.find(
      (ca: IComputedAggregate): boolean => ca.meta.id === sessionId
    ) as IComputedAggregate;

    let metricOneMax: number;
    /// if DSI template choose the higher of braking and propulsive force
    if (templateId === DSI_TEMPLATE_ID) {
      const brakingMax = Boolean(one)
        ? determineMax(one, 'peakBrakingForce', 1)
        : null;

      const propulsiveMax = Boolean(one)
        ? determineMax(one, 'peakPropulsiveForce', 1)
        : null;
      metricOneMax = Math.abs(
        brakingMax > propulsiveMax ? brakingMax : propulsiveMax
      );
    } else {
      metricOneMax = Boolean(one)
        ? Math.abs(
            determineMax(
              one,
              metricMeta.metricOne.entity.id,
              metricMeta.metricOne.entity.direction
            )
          )
        : null;
    }
    const metricTwoMax = Boolean(two)
      ? Math.abs(
          determineMax(
            two,
            metricMeta.metricTwo.entity.id,
            metricMeta.metricTwo.entity.direction
          )
        )
      : null;

    return {
      timestamp: +sessionId,
      metricOne: {
        metric: {
          ...correctUnitsIfImperial(metricMeta.metricOne.entity, one),
          value: metricOneMax
            ? roundValue(
                metricOneMax,
                metricMeta.metricOne.entity.roundingPrecision
              )
            : null,
        },
        label: metricMeta.metricOne.label,
      },
      metricTwo: {
        metric: {
          ...correctUnitsIfImperial(metricMeta.metricTwo.entity, two),
          value: metricTwoMax
            ? roundValue(
                metricTwoMax,
                metricMeta.metricTwo.entity.roundingPrecision
              )
            : null,
        },
        label: metricMeta.metricTwo.label,
      },
      ratio:
        metricOneMax && metricTwoMax
          ? roundValue(metricOneMax / metricTwoMax, 2)
          : null,
    } as IRatio;
  });

  if (filter) return allData.filter((r: IRatio): boolean => r.ratio !== null);
  return allData;
};

const correctUnitsIfImperial = (
  metricFromSettings: IMetric,
  aggregate: IComputedAggregate
): IMetric => {
  const metricFromData = aggregate?.data[metricFromSettings.id];
  if (metricFromSettings?.units === 'm' && metricFromData?.units === 'in') {
    return {
      ...metricFromSettings,
      units: metricFromData.units,
    };
  } else {
    return metricFromSettings;
  }
};

/**
 * @description returns the highest peak force from either the braking or propulsive phase
 * @param aggregate
 * @returns
 */
const determineMax = (
  aggregate: IComputedAggregate,
  metricId: string,
  metricDirection: number
): number | null => {
  if (metricDirection === 1) {
    return aggregate.data[metricId as string]?.topValues[0];
  }
  if (metricDirection === -1) {
    return aggregate.data[metricId as string]?.bottomValues[0];
  }
  return null;
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateState = (
  settingsState: IRatioReportSettingsState,
  resultList: IRatioResult[]
): IValidationResult => {
  const settingsValidation = validateSettings(settingsState);
  if (settingsValidation.isValid) {
    if (resultList.length === 0) {
      return {
        isValid: false,
        errorCode: 'insufficient-data',
      };
    } else {
      return {
        isValid: true,
      };
    }
  } else {
    return settingsValidation;
  }
};

const validateSettings = (
  settings: IRatioReportSettingsState
): IValidationResult => {
  if (
    !isValidDateRange({
      dateType: { label: 'Custom', value: 'custom' },
      dateRange: settings?.timeframe,
    })
  ) {
    return {
      isValid: false,
      errorCode: 'missing-date-range',
    };
  }

  if (!settings.selected?.id) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }

  return {
    isValid: true,
  };
};

interface IMetricMeta {
  metricOne: {
    hash: string;
    entity: IMetric;
    label: string;
  };
  metricTwo: {
    hash: string;
    entity: IMetric;
    label: string;
  };
}
